import { createBrowserRouter, redirect, type RouteObject } from "react-router";
import { AppLayout } from "@/components/layout/AppLayout";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { RequireAuth } from "@/app/require-auth";
import { RootRedirect } from "@/app/root-redirect";
import { RouteError } from "@/app/route-error";

/** Each screen is its own chunk, so the first paint loads one screen, not all of them. */
function screen(load: () => Promise<{ default: React.ComponentType }>) {
  return async () => ({ Component: (await load()).default });
}

/** Routes follow docs/05-design/information-architecture.md §4. */
export const routes: RouteObject[] = [
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          {
            path: "/sign-in/*",
            lazy: async () => ({ Component: (await import("@/app/auth-pages")).SignInPage }),
          },
          {
            path: "/sign-up/*",
            lazy: async () => ({ Component: (await import("@/app/auth-pages")).SignUpPage }),
          },
          {
            path: "/invite/:token",
            lazy: screen(() => import("@/features/invite/AcceptInvitePage")),
          },
          // Public on purpose: a supervisor opens the review link before they have an account.
          // Reading needs nothing; the card at the bottom is where signing asks for a sign-in.
          {
            path: "/review/:token",
            lazy: screen(() => import("@/features/review/ReviewLinkPage")),
          },
          // Public on purpose: the link printed on every signed PDF (signature spec §11.2).
          { path: "/verify", lazy: screen(() => import("@/features/verify/VerifyPage")) },
          { path: "/verify/:hash", lazy: screen(() => import("@/features/verify/VerifyPage")) },
          // Public: the footer links here from both shells, and nothing on the page reads
          // auth data. Behind RequireAuth it bounced signed-out visitors to sign-in.
          { path: "/help", lazy: screen(() => import("@/features/help/HelpPage")) },
          {
            path: "/help/:slug",
            lazy: screen(() => import("@/features/help/HelpArticlePage")),
          },
          // Public: the unsubscribe link in lifecycle and marketing emails. The token is the key.
          {
            path: "/unsubscribe/:token",
            lazy: screen(() => import("@/features/admin/comms/UnsubscribePage")),
          },
          { path: "/legal/terms", lazy: screen(() => import("@/features/legal/TermsPage")) },
          { path: "/legal/privacy", lazy: screen(() => import("@/features/legal/PrivacyPage")) },
          { path: "/403", lazy: screen(() => import("@/features/errors/ForbiddenPage")) },
          {
            path: "/session-expired",
            lazy: screen(() => import("@/features/errors/SessionExpiredPage")),
          },
          { path: "/maintenance", lazy: screen(() => import("@/features/errors/MaintenancePage")) },
          { path: "*", lazy: screen(() => import("@/features/errors/NotFoundPage")) },
        ],
      },
      {
        element: <RequireAuth />,
        children: [
          // The admin console: its own shell, and the API refuses anyone who is not an admin.
          {
            path: "/admin",
            lazy: screen(() => import("@/features/admin/AdminLayout")),
            children: [
              { index: true, lazy: screen(() => import("@/features/admin/queues/AdminHomePage")) },
              ...(
                [
                  ["queues/:queue", () => import("@/features/admin/queues/QueuePage")],
                  ["search", () => import("@/features/admin/tenants/SearchPage")],
                  ["tenants/:userId", () => import("@/features/admin/tenants/TenantPage")],
                  ["payments", () => import("@/features/admin/money/PaymentsPage")],
                  ["payments/:paymentId", () => import("@/features/admin/money/PaymentDetailPage")],
                  ["refunds", () => import("@/features/admin/money/RefundsPage")],
                  ["webhooks", () => import("@/features/admin/money/WebhooksPage")],
                  ["coupons", () => import("@/features/admin/coupons/CouponsPage")],
                  ["reconciliation", () => import("@/features/admin/reports/ReconciliationPage")],
                  ["reports", () => import("@/features/admin/reports/ReportsPage")],
                  ["reports/:report", () => import("@/features/admin/reports/ReportsPage")],
                  ["comms", () => import("@/features/admin/comms/CommsPage")],
                  [
                    "comms/templates/:templateId",
                    () => import("@/features/admin/comms/TemplateEditPage"),
                  ],
                  [
                    "comms/campaigns/:campaignId",
                    () => import("@/features/admin/comms/CampaignPage"),
                  ],
                  ["jobs", () => import("@/features/admin/system/JobsPage")],
                  ["flags", () => import("@/features/admin/system/FlagsPage")],
                  ["config", () => import("@/features/admin/system/ConfigPage")],
                  ["privacy", () => import("@/features/admin/system/PrivacyPage")],
                  ["audit", () => import("@/features/admin/system/AuditPage")],
                  ["admins", () => import("@/features/admin/system/AdminsPage")],
                ] as const
              ).map(([path, load]) => ({ path, lazy: screen(load) })),
            ],
          },
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <RootRedirect /> },
              {
                path: "/onboarding",
                lazy: screen(() => import("@/features/onboarding/OnboardingPage")),
              },
              {
                path: "/t/:trackId",
                lazy: screen(() => import("@/features/dashboard/DashboardPage")),
              },
              { path: "/t/:trackId/log", lazy: screen(() => import("@/features/log/LogListPage")) },
              {
                path: "/t/:trackId/log/new",
                lazy: screen(() => import("@/features/log/EntryFormPage")),
              },
              {
                path: "/t/:trackId/log/:entryId/edit",
                lazy: screen(() => import("@/features/log/EntryFormPage")),
              },
              {
                path: "/t/:trackId/log/bulk",
                lazy: screen(() => import("@/features/bulk/BulkEntryPage")),
              },
              {
                path: "/t/:trackId/import",
                lazy: screen(() => import("@/features/imports/ImportWizardPage")),
              },
              {
                path: "/t/:trackId/import/:jobId",
                lazy: screen(() => import("@/features/imports/ImportWizardPage")),
              },
              {
                path: "/t/:trackId/periods",
                lazy: screen(() => import("@/features/periods/PeriodsPage")),
              },
              {
                path: "/t/:trackId/periods/:periodId",
                lazy: screen(() => import("@/features/periods/PeriodDetailPage")),
              },
              {
                path: "/t/:trackId/periods/:periodId/amend",
                lazy: screen(() => import("@/features/periods/AmendPage")),
              },
              {
                path: "/t/:trackId/people",
                lazy: screen(() => import("@/features/sites/SupervisorsPage")),
              },
              {
                path: "/t/:trackId/people/sites",
                lazy: screen(() => import("@/features/sites/SitesPage")),
              },
              {
                path: "/t/:trackId/exports",
                lazy: screen(() => import("@/features/exports/ExportsPage")),
              },
              // The rules in force on this track, with every citation behind every figure.
              {
                path: "/t/:trackId/rules",
                lazy: screen(() => import("@/features/rules/RulesPage")),
              },
              // A manual track's own targets, typed by the practitioner.
              {
                path: "/t/:trackId/targets",
                lazy: screen(() => import("@/features/rules/EditTargetsPage")),
              },
              // The board's copy, on screen before it is on paper. print.css hides the shell.
              {
                path: "/print/periods/:periodId",
                lazy: screen(() => import("@/features/print/PrintPreviewPage")),
              },
              {
                path: "/supervise",
                lazy: screen(() => import("@/features/supervise/ReviewQueuePage")),
              },
              {
                path: "/supervise/periods/:periodId",
                lazy: screen(() => import("@/features/supervise/PeriodReviewPage")),
              },
              {
                path: "/supervise/periods/:periodId/sign",
                lazy: screen(() => import("@/features/supervise/SignPage")),
              },
              // The review link's claim answers with this shorter path. One alias beats two
              // spellings of the sign screen.
              {
                path: "/supervise/sign/:periodId",
                loader: ({ params }) => redirect(`/supervise/periods/${params.periodId}/sign`),
              },
              {
                path: "/supervise/signed",
                lazy: screen(() => import("@/features/supervise/SignedRecordsPage")),
              },
              // The design gallery: every component with sample data, in both themes.
              { path: "/design", lazy: screen(() => import("@/features/design/DesignPage")) },
              { path: "/account", lazy: screen(() => import("@/features/account/AccountPage")) },
              // The plan and the payment. Every paywall card sends people here.
              { path: "/billing", lazy: screen(() => import("@/features/billing/BillingPage")) },
              {
                path: "/settings",
                lazy: screen(() => import("@/features/settings/SettingsPage")),
              },
              {
                path: "/notifications",
                lazy: screen(() => import("@/features/notifications/NotificationsPage")),
              },
              // Dev builds only. Reads your own dev mailbox, never anyone else's.
              // MODE, not DEV: app/.env sets NODE_ENV=development, which Vite reads from envDir
              // and which makes `import.meta.env.DEV` true even in a production build.
              ...(import.meta.env.MODE === "development"
                ? [
                    {
                      path: "/dev/mail",
                      lazy: screen(() => import("@/features/devmail/DevMailPage")),
                    },
                  ]
                : []),
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
